import { useEffect, useState } from 'react';

/**
 * How much the visual viewport has to shrink before we call it the on-screen
 * keyboard rather than browser chrome (iOS's URL bar collapsing moves it by
 * well under this).
 */
const KEYBOARD_MIN_HEIGHT_PX = 150;

/** Focus targets that raise the on-screen keyboard. */
function isTextEntry(el: Element): el is HTMLElement {
  if (el instanceof HTMLTextAreaElement) return true;
  if (!(el instanceof HTMLInputElement)) return false;
  return ![
    'button',
    'checkbox',
    'radio',
    'file',
    'range',
    'submit',
    'color',
  ].includes(el.type);
}

/**
 * Bring the focused field into the visible part of the overlay. A field can
 * opt into being scrolled to the TOP instead of centred by sitting inside a
 * `[data-keyboard-scroll="start"]` wrapper — used where a dropdown opens
 * below the field and needs the room (the child form's school search).
 */
function revealFocused(overlay: HTMLElement) {
  const active = document.activeElement;
  if (!active || !overlay.contains(active) || !isTextEntry(active)) return;
  const anchor = active.closest<HTMLElement>('[data-keyboard-scroll="start"]');
  // scrollIntoView may not exist in test environments (JSDOM).
  if (anchor) anchor.scrollIntoView?.({ block: 'start' });
  else active.scrollIntoView?.({ block: 'center' });
}

function clearPin(overlay: HTMLElement) {
  overlay.style.top = '';
  overlay.style.height = '';
  overlay.style.bottom = '';
}

/**
 * Keeps a full-screen `position: fixed` overlay usable while the on-screen
 * keyboard is open (YAR-508).
 *
 * iOS (Safari, and Chrome — same WebKit) does not shrink the layout viewport
 * for the keyboard: a `fixed inset-0` overlay stays full height and the
 * keyboard simply covers its lower half, and WebKit does not reliably scroll
 * a field inside a fixed element into view. So the overlay is pinned to the
 * VISUAL viewport — the part of the screen above the keyboard — and the
 * focused field is scrolled into it whenever the keyboard opens or focus
 * moves. `interactive-widget=resizes-content` would do this declaratively, but
 * WebKit ignores it.
 *
 * Takes the overlay ELEMENT (from a callback ref / `useState` setter), not a
 * ref object: the child form swaps its overlay out for the avatar pickers and
 * back, and an effect keyed on a stable ref object would never re-attach to
 * the new node.
 *
 * Returns whether the keyboard is open, so the overlay can drop chrome (a
 * sticky footer) that would otherwise eat the little space left.
 *
 * A no-op where `visualViewport` is missing (JSDOM, very old browsers): the
 * overlay keeps its `inset-0` layout.
 */
export function useKeyboardAwareOverlay(overlay: HTMLElement | null): boolean {
  const [keyboardOpen, setKeyboardOpen] = useState(false);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!overlay || !vv) return;

    let frame = 0;
    let wasOpen = false;

    const sync = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        // Pinch-zoom also shrinks the visual viewport. Following it then would
        // reflow the form into the zoomed region (and read as a keyboard), so
        // a zoomed page keeps the plain `inset-0` layout.
        if (vv.scale > 1.01) {
          clearPin(overlay);
          setKeyboardOpen(false);
          wasOpen = false;
          return;
        }
        overlay.style.top = `${vv.offsetTop}px`;
        overlay.style.height = `${vv.height}px`;
        overlay.style.bottom = 'auto';
        const open =
          document.documentElement.clientHeight - vv.height >=
          KEYBOARD_MIN_HEIGHT_PX;
        setKeyboardOpen(open);
        // Only on the keyboard OPENING: re-revealing on every resize/scroll
        // would yank the overlay back while the user scrolls it.
        if (open && !wasOpen) revealFocused(overlay);
        wasOpen = open;
      });
    };

    // Focus moving between fields while the keyboard is already up fires no
    // viewport resize, so it needs its own reveal. Deferred a frame so it runs
    // after the browser's own focus scrolling, not before it.
    const onFocusIn = () => {
      if (!wasOpen) return;
      requestAnimationFrame(() => revealFocused(overlay));
    };

    sync();
    vv.addEventListener('resize', sync);
    vv.addEventListener('scroll', sync);
    overlay.addEventListener('focusin', onFocusIn);
    return () => {
      cancelAnimationFrame(frame);
      vv.removeEventListener('resize', sync);
      vv.removeEventListener('scroll', sync);
      overlay.removeEventListener('focusin', onFocusIn);
      clearPin(overlay);
    };
  }, [overlay]);

  return keyboardOpen;
}
